import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useRestaurantContext } from "@/contexts/RestaurantContext";
import { toast } from "sonner";

export interface TaxRule {
  id: string;
  restaurant_id: string;
  name: string;
  description: string | null;
  ncm: string;
  cest: string | null;
  cfop: string;
  csosn: string;
  cst_icms: string | null;
  icms_aliquota: number;
  icms_reducao_base: number;
  cst_pis: string;
  pis_aliquota: number;
  cst_cofins: string;
  cofins_aliquota: number;
  origem: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export function useTaxRules() {
  const { activeRestaurant } = useRestaurantContext();
  const [taxRules, setTaxRules] = useState<TaxRule[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchTaxRules = useCallback(async () => {
    if (!activeRestaurant?.id) return;

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("tax_rules")
        .select("*")
        .eq("restaurant_id", activeRestaurant.id)
        .order("name");

      if (error) throw error;
      setTaxRules(data || []);
    } catch (error) {
      console.error("Erro ao buscar regras tributárias:", error);
    } finally {
      setLoading(false);
    }
  }, [activeRestaurant?.id]);

  useEffect(() => {
    fetchTaxRules();
  }, [fetchTaxRules]);

  const createTaxRule = async (data: Partial<TaxRule>) => {
    if (!activeRestaurant?.id) return false;

    try {
      const { error } = await supabase
        .from("tax_rules")
        .insert({
          restaurant_id: activeRestaurant.id,
          name: data.name || "",
          ncm: data.ncm || "",
          cfop: data.cfop || "5102",
          description: data.description,
          cest: data.cest,
          csosn: data.csosn,
          cst_icms: data.cst_icms,
          icms_aliquota: data.icms_aliquota,
          icms_reducao_base: data.icms_reducao_base,
          cst_pis: data.cst_pis,
          pis_aliquota: data.pis_aliquota,
          cst_cofins: data.cst_cofins,
          cofins_aliquota: data.cofins_aliquota,
          origem: data.origem,
          is_active: data.is_active ?? true,
        });

      if (error) throw error;

      toast.success("Regra tributária criada!");
      await fetchTaxRules();
      return true;
    } catch (error) {
      console.error("Erro ao criar regra tributária:", error);
      toast.error("Erro ao criar regra");
      return false;
    }
  };

  const updateTaxRule = async (id: string, data: Partial<TaxRule>) => {
    try {
      const { error } = await supabase
        .from("tax_rules")
        .update({ ...data, updated_at: new Date().toISOString() })
        .eq("id", id);

      if (error) throw error;

      toast.success("Regra tributária atualizada!");
      await fetchTaxRules();
      return true;
    } catch (error) {
      console.error("Erro ao atualizar regra tributária:", error);
      toast.error("Erro ao atualizar regra");
      return false;
    }
  };

  const deleteTaxRule = async (id: string) => {
    try {
      const { error } = await supabase
        .from("tax_rules")
        .delete()
        .eq("id", id);

      if (error) throw error;

      toast.success("Regra tributária excluída!");
      await fetchTaxRules();
      return true;
    } catch (error) {
      console.error("Erro ao excluir regra tributária:", error);
      toast.error("Erro ao excluir regra");
      return false;
    }
  };

  const applyTaxRuleToProducts = async (ruleId: string, productIds: string[]) => {
    if (!productIds.length) return false;

    try {
      const { error } = await supabase
        .from("products")
        .update({ tax_rule_id: ruleId })
        .in("id", productIds);

      if (error) throw error;

      toast.success(`Regra aplicada a ${productIds.length} produto(s)!`);
      return true;
    } catch (error) {
      console.error("Erro ao aplicar regra:", error);
      toast.error("Erro ao aplicar regra");
      return false;
    }
  };

  return {
    taxRules,
    loading,
    fetchTaxRules,
    createTaxRule,
    updateTaxRule,
    deleteTaxRule,
    applyTaxRuleToProducts,
  };
}
